import { entityKeyFor } from '../reducers/annotations.reducer';

// Base selectors
const getAnnotationsState = state => state.annotations || {};
const getByEntityMap = state => getAnnotationsState(state).byEntity || {};
const getVisible = state => getAnnotationsState(state).visible;
const getCreateMode = state => !!getAnnotationsState(state).createMode;
const getActiveEntity = state => getAnnotationsState(state).activeEntity || null;
const getVisibleTypes = state => getAnnotationsState(state).visibleTypes || [];
const getFilters = state => getAnnotationsState(state).filters || {};

const EMPTY_ANNOTATIONS = [];
const EMPTY_ENTITY = { byId: {}, allIds: [], loading: false, error: null, currentRequestId: 0, lastFetchedRange: null };

// The per-entity data bucket for a given entity, or a stable empty bucket. The
// annotations slice is partitioned by `annotatable_type:annotatable_id` so
// concurrent charts for different entities can't clobber each other.
const getEntitySlice = (state, entityType, entityId) => {
  const key = entityKeyFor(entityType, entityId);
  if (!key) return EMPTY_ENTITY;
  return getByEntityMap(state)[key] || EMPTY_ENTITY;
};

// Aggregate byId across every entity bucket. Back-compat for the global
// selectors below; the primary consumers use the entity-scoped variants.
const getById = (state) => {
  const buckets = getByEntityMap(state);
  return Object.keys(buckets).reduce((acc, key) => Object.assign(acc, buckets[key].byId || {}), {});
};
const getAllIds = (state) => {
  const buckets = getByEntityMap(state);
  return Object.keys(buckets).reduce((acc, key) => acc.concat(buckets[key].allIds || []), []);
};

// Aggregate loading/error across buckets (any bucket loading; first bucket error).
const getLoading = (state) => {
  const buckets = getByEntityMap(state);
  return Object.keys(buckets).some(key => buckets[key].loading);
};
const getError = (state) => {
  const buckets = getByEntityMap(state);
  const errored = Object.keys(buckets).find(key => buckets[key].error);
  return errored ? buckets[errored].error : null;
};

// Loading state for a single entity's chart.
const getLoadingForEntity = (state, entityType, entityId) => !!getEntitySlice(state, entityType, entityId).loading;

// Single-entry memoizer keyed on referential equality of `inputsFn`'s output
// (the project has no `reselect`). Stable output refs stop connected charts from
// re-rendering on unrelated dispatches. Use the `make*` factories for a
// per-instance cache when args vary across component instances.
const memoizeByInputs = (inputsFn, computeFn) => {
  let lastInputs = null;
  let lastResult;
  return (...args) => {
    const inputs = inputsFn(...args);
    if (
      lastInputs
      && lastInputs.length === inputs.length
      && lastInputs.every((value, i) => value === inputs[i])
    ) {
      return lastResult;
    }
    lastInputs = inputs;
    lastResult = computeFn(inputs, ...args);
    return lastResult;
  };
};

// Selector to get all annotations (across every entity) as an array.
export const getAllAnnotations = (state) => {
  const byId = getById(state);
  const allIds = getAllIds(state);
  return allIds.map(id => byId[id]).filter(Boolean);
};

const computeFilteredAnnotations = (all, visibleTypes, filters, globalVisible) => {
  if (!globalVisible) return EMPTY_ANNOTATIONS;

  // Build latest-by-task maps for defect_label and machine_condition_change
  const latestByTask = (items, keyFn) => {
    const map = new Map();
    items.forEach((item) => {
      const key = keyFn(item);
      if (!key && key !== 0) return;
      const prev = map.get(key);
      const prevTs = prev ? Date.parse(prev.start_time) : -Infinity;
      const curTs = Date.parse(item.start_time);
      if (!Number.isFinite(curTs)) return;
      if (!prev || curTs >= prevTs) {
        map.set(key, item);
      }
    });
    return map;
  };

  const tasks = all.filter(a => a && a.type === 'task');
  const defects = all.filter(a => a && a.type === 'defect_label');
  const conditions = all.filter(a => a && a.type === 'machine_condition_change');
  const others = all.filter(a => a && a.type !== 'task' && a.type !== 'defect_label' && a.type !== 'machine_condition_change');

  const defectsByTaskId = latestByTask(defects, d => d && d.base_metadata && d.base_metadata.task_id);
  const conditionsByTaskId = latestByTask(conditions, c => c && c.base_metadata && c.base_metadata.task_id);

  // Attach merged info to task annotations (without mutating originals)
  const mergedTasks = tasks.map((t) => {
    const copy = { ...t };
    const taskId = t && t.source_id;
    if (taskId && defectsByTaskId.has(taskId)) {
      copy.mergedDefectLabel = defectsByTaskId.get(taskId);
    }
    if (taskId && conditionsByTaskId.has(taskId)) {
      copy.mergedMachineCondition = conditionsByTaskId.get(taskId);
    }
    return copy;
  });

  // Exclude standalone defect_label and machine_condition_change from output
  const merged = [...others, ...mergedTasks];

  // Apply UI filters after merging
  return merged.filter((a) => {
    if (!visibleTypes.includes(a.type)) return false;
    if (filters && filters.severity && filters.severity.length > 0 && !filters.severity.includes(a.severity)) return false;
    if (filters && filters.search && filters.search.trim()) {
      const s = filters.search.toLowerCase();
      const desc = (a.description || '').toLowerCase();
      if (!desc.includes(s)) return false;
    }
    if (filters && filters.dateRange) {
      const t = new Date(a.start_time);
      if (filters.dateRange && filters.dateRange.start && t < filters.dateRange.start) return false;
      if (filters.dateRange && filters.dateRange.end && t > filters.dateRange.end) return false;
    }
    return true;
  });
};

// Aggregate (all-entity) filtered annotations. One instance per component.
export const makeGetFilteredAnnotations = () => memoizeByInputs(
  state => [
    getByEntityMap(state),
    getVisibleTypes(state),
    getFilters(state),
    getVisible(state)
  ],
  ([, visibleTypes, filters, globalVisible], state) => {
    if (!globalVisible) return EMPTY_ANNOTATIONS;
    return computeFilteredAnnotations(getAllAnnotations(state), visibleTypes, filters, globalVisible);
  }
);

export const getFilteredAnnotations = makeGetFilteredAnnotations();

// Entity-scoped filtered annotations: reads only the entity's own bucket, so a
// chart never sees another entity's notes. One instance per component.
export const makeGetFilteredAnnotationsForEntity = () => memoizeByInputs(
  (state, entityType, entityId) => {
    const slice = getEntitySlice(state, entityType, entityId);
    return [slice.byId, slice.allIds, getVisibleTypes(state), getFilters(state), getVisible(state)];
  },
  ([byId, allIds, visibleTypes, filters, globalVisible]) => {
    if (!globalVisible) return EMPTY_ANNOTATIONS;
    const all = allIds.map(id => byId[id]).filter(Boolean);
    return computeFilteredAnnotations(all, visibleTypes, filters, globalVisible);
  }
);

const filterAnnotationsByRange = (annotations, timeRange) => {
  const startMs = timeRange && timeRange.start ? new Date(timeRange.start).getTime() : null;
  const endMs = timeRange && timeRange.end ? new Date(timeRange.end).getTime() : null;
  if (startMs === null || endMs === null || Number.isNaN(startMs) || Number.isNaN(endMs)) {
    return annotations;
  }
  return annotations.filter((annotation) => {
    const annotationStart = new Date(annotation.start_time).getTime();
    const annotationEnd = annotation.end_time
      ? new Date(annotation.end_time).getTime()
      : annotationStart;
    return annotationStart <= endMs && annotationEnd >= startMs;
  });
};

// Keyed on the filtered list plus the range's start/end ms, so a fresh timeRange
// object with the same bounds doesn't bust the cache. One instance per component.
export const makeGetAnnotationsForTimeRange = () => {
  const getFiltered = makeGetFilteredAnnotations();
  return memoizeByInputs(
    (state, timeRange) => [
      getFiltered(state),
      timeRange && timeRange.start ? new Date(timeRange.start).getTime() : null,
      timeRange && timeRange.end ? new Date(timeRange.end).getTime() : null
    ],
    ([annotations], state, timeRange) => filterAnnotationsByRange(annotations, timeRange)
  );
};

export const getAnnotationsForTimeRange = makeGetAnnotationsForTimeRange();

// Entity-scoped time-range selector: the entity's own annotations, filtered to
// the chart's visible window. This is what AnnotatedChart/AnnotationsList use so
// concurrent charts for different entities never render each other's notes.
export const makeGetAnnotationsForEntityTimeRange = () => {
  const getFilteredForEntity = makeGetFilteredAnnotationsForEntity();
  return memoizeByInputs(
    (state, entityType, entityId, timeRange) => [
      getFilteredForEntity(state, entityType, entityId),
      timeRange && timeRange.start ? new Date(timeRange.start).getTime() : null,
      timeRange && timeRange.end ? new Date(timeRange.end).getTime() : null
    ],
    ([annotations], state, entityType, entityId, timeRange) => filterAnnotationsByRange(annotations, timeRange)
  );
};

// Selector to get annotations grouped by type
export const getAnnotationsByType = (state) => {
  const annotations = getFilteredAnnotations(state);

  return annotations.reduce((groups, annotation) => {
    const type = annotation.type;
    if (!groups[type]) {
      groups[type] = [];
    }
    groups[type].push(annotation);
    return groups;
  }, {});
};

// Selector to get annotation counts by type
export const getAnnotationCountsByType = (state) => {
  const annotationsByType = getAnnotationsByType(state);
  const counts = {};
  Object.keys(annotationsByType).forEach((type) => {
    counts[type] = annotationsByType[type].length;
  });
  return counts;
};

// Selector to get annotations for a specific entity (reads that entity's bucket).
export const getAnnotationsForEntity = (state, entityType, entityId) => {
  if (!entityType || !entityId) return getFilteredAnnotations(state);
  const slice = getEntitySlice(state, entityType, entityId);
  const all = (slice.allIds || []).map(id => slice.byId[id]).filter(Boolean);
  return computeFilteredAnnotations(all, getVisibleTypes(state), getFilters(state), getVisible(state));
};

// Check if we need to fetch annotations for the given parameters. Scoped to the
// entity's own bucket, so one chart's fetch state can't suppress another's.
export const shouldFetchAnnotations = (state, params) => {
  const slice = getEntitySlice(state, params.annotatable_type, params.annotatable_id);

  if (slice.loading) return false; // Already loading for this entity

  const lastRange = slice.lastFetchedRange;
  if (!lastRange) return true; // No previous fetch for this entity

  // Check if time range has changed
  if (lastRange.start_time !== params.start_time || lastRange.end_time !== params.end_time) return true;

  // Check if annotation types filter has changed
  const lastTypes = (lastRange.types || []).slice().sort();
  const currentTypes = (params.types || []).slice().sort();
  if (JSON.stringify(lastTypes) !== JSON.stringify(currentTypes)) return true;

  return false;
};

// Export simple selectors
export {
  getAnnotationsState,
  getByEntityMap,
  getEntitySlice,
  getById,
  getAllIds,
  getVisible,
  getCreateMode,
  getActiveEntity,
  getVisibleTypes,
  getFilters,
  getLoading,
  getLoadingForEntity,
  getError
};
